// Shared portrait detection for game image surfaces.
//
// Staff can upload client-side baked portrait (3:4) event images. Those baked
// exports carry neutral composition metadata (scale 100, x/y 0). Surfaces that
// render game images in a fixed landscape box use this mixin to letterbox a
// baked portrait image (object-fit: contain on its background color) so the
// full composition shows instead of being center-cropped. Legacy free-position
// images were authored against the landscape box and are left untouched.
//
// Usage in a component:
//   import gameImageOrientation from '@/mixins/gameImageOrientation'
//   mixins: [gameImageOrientation]
//   // call this.probeImageOrientation(list) when image data loads/changes
//   // bind :style="{ 'object-fit': imageFitInBox(item) }" on the <img>
import { twinUrl } from '../util/imageUrl.js'

export default {
  data() {
    return {
      // image url -> true when the file's natural dimensions are portrait
      portraitImages: {}
    }
  },
  methods: {
    // Only baked exports (neutral metadata) may change fit. Legacy images keep
    // their stored composition, so they must render exactly as before.
    isNeutralImage(item) {
      return Number(item.scale == null ? 100 : item.scale) === 100 && !Number(item.x || 0) && !Number(item.y || 0)
    },
    // Read natural dimensions from each url once, into the reactive map, keyed by
    // the stored url. The <img> renders the resized twin (v-twin-src), so probe the
    // twin first and resolve from browser cache; if the twin is missing, probe the
    // original, which is what the <img> falls back to.
    probeImageOrientation(items) {
      if (!Array.isArray(items)) return
      items.forEach(item => {
        if (!item || !item.url || this.portraitImages[item.url] !== undefined) return
        const url = item.url
        const record = (probe) => this.$set(this.portraitImages, url, probe.naturalHeight > probe.naturalWidth)
        const probeOriginal = () => {
          const probe = new Image()
          probe.onload = () => record(probe)
          probe.onerror = () => this.$set(this.portraitImages, url, false)
          probe.src = url
        }
        const twin = twinUrl(url)
        if (twin === url) return probeOriginal()
        const probe = new Image()
        probe.onload = () => record(probe)
        probe.onerror = probeOriginal
        probe.src = twin
      })
    },
    // object-fit for a fixed landscape box: contain a baked portrait image,
    // else the image's stored fit (unchanged, including absent -> undefined).
    imageFitInBox(item) {
      if (!item) return 'cover'
      if (this.portraitImages[item.url] && this.isNeutralImage(item)) return 'contain'
      return item.object_fit
    }
  }
}
